import { useMemo } from "react";

import { useCachedDisplayData, useCollectionDataListener } from "../firebase";

const MAX_ENTRIES = 5;

const toMillis = (value) => {
  if (!value) {
    return 0;
  }
  if (typeof value === "number") {
    return value;
  }
  if (typeof value.toMillis === "function") {
    return value.toMillis();
  }
  if (value.seconds) {
    return value.seconds * 1000;
  }
  return new Date(value).getTime() || 0;
};

const buildBuyerNameMap = (customers) =>
  Object.fromEntries(
    (customers || []).map(({ buyerId, buyerName }) => [buyerId, buyerName])
  );

export const buildPendingBomRequestSummary = (lifecycleEvents, customers) => {
  const buyerNameMap = buildBuyerNameMap(customers);
  return (lifecycleEvents || [])
    .filter(({ bomStatus }) => bomStatus === "requested")
    .map((event) => ({
      lifecycleEventId: event.id,
      productId: event.productId,
      productName: event.productName || "Unnamed Product",
      buyerName: buyerNameMap[event.ownerId] || event.ownerName || null,
      dateRequested: toMillis(event.dateRequested)
    }))
    .sort((a, b) => b.dateRequested - a.dateRequested)
    .slice(0, MAX_ENTRIES);
};

const mergeProductSources = (vendorAssigned, ownCatalog) => {
  const ownIds = new Set((ownCatalog || []).map(({ id }) => id));
  const dedupedVendorAssigned = (vendorAssigned || []).filter(
    ({ id }) => !ownIds.has(id)
  );
  return [...(ownCatalog || []), ...dedupedVendorAssigned];
};

export const isSharedProduct = ({ bomShared, reportShared }) =>
  Boolean(bomShared || reportShared);

const isReceivedBomRequest = ({ bomStatus }) =>
  Boolean(bomStatus) && bomStatus !== "pending";

export const buildProductsSummary = (
  vendorAssignedProducts,
  ownCatalogProducts,
  lifecycleEvents
) => {
  const merged = mergeProductSources(
    vendorAssignedProducts,
    ownCatalogProducts
  );
  const safeProducts = merged.filter(
    (p) => !p?.archived && !p?.parentProductId
  );
  const eventByProductId = Object.fromEntries(
    (lifecycleEvents || []).map((event) => [event.productId, event])
  );
  const items = safeProducts.slice(0, MAX_ENTRIES).map((product) => ({
    productId: product.id,
    productName: product.name || "Unnamed Product",
    bomStatus: eventByProductId[product.id]?.bomStatus || null,
    customersCount: 0,
    sharedCount: 0
  }));
  const receivedBomRequestsCount = (lifecycleEvents || []).filter(
    isReceivedBomRequest
  ).length;
  return {
    count: safeProducts.length,
    receivedBomRequestsCount,
    items
  };
};

const buildReceivedProductsCount = (vendorRelationships) =>
  (vendorRelationships || []).reduce(
    (sum, rel) => sum + (rel.sharedProducts || []).filter(isSharedProduct).length,
    0
  );

export const useProductsTabContext = () => {
  const [vendorAssignedProducts] = useCachedDisplayData(
    "vendor-assigned-products"
  );
  const [ownCatalogProducts] = useCachedDisplayData("product-components");
  const [lifecycleEvents] = useCachedDisplayData(
    "vendor-assigned-product-lifecycle-events"
  );
  const [vendorRelationships] = useCachedDisplayData("vendor-relationships");
  const [customers] = useCollectionDataListener("customers");

  return useMemo(
    () => ({
      productsSummary: buildProductsSummary(
        vendorAssignedProducts,
        ownCatalogProducts,
        lifecycleEvents
      ),
      pendingBomRequests: buildPendingBomRequestSummary(
        lifecycleEvents,
        customers
      ),
      receivedProductsCount: buildReceivedProductsCount(vendorRelationships),
      customers
    }),
    [
      vendorAssignedProducts,
      ownCatalogProducts,
      lifecycleEvents,
      vendorRelationships,
      customers
    ]
  );
};
